import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import { useLocation } from 'react-router-dom'
import {
  applyDocumentTheme,
  readSystemPrefersDark,
  readThemePreference,
  toggleThemePreference,
  writeThemePreference,
  type ResolvedTheme,
  type ThemePreference,
} from '@/lib/theme'

type ThemeContextValue = {
  preference: ThemePreference
  resolved: ResolvedTheme
  /** Flip the binary org-menu switch (stores an explicit light/dark override). */
  toggleDarkMode: () => void
  setPreference: (preference: ThemePreference) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  const [preference, setPreferenceState] = useState<ThemePreference>(() =>
    readThemePreference(),
  )
  const [systemPrefersDark, setSystemPrefersDark] = useState(() =>
    readSystemPrefersDark(),
  )
  const [resolved, setResolved] = useState<ResolvedTheme>('light')

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => setSystemPrefersDark(mq.matches)
    onChange()
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    setResolved(applyDocumentTheme(preference, systemPrefersDark, pathname))
  }, [preference, systemPrefersDark, pathname])

  function setPreference(next: ThemePreference) {
    writeThemePreference(next)
    setPreferenceState(next)
  }

  function toggleDarkMode() {
    const current = applyDocumentTheme(preference, systemPrefersDark, pathname)
    setPreference(toggleThemePreference(current))
  }

  return (
    <ThemeContext.Provider
      value={{ preference, resolved, toggleDarkMode, setPreference }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) {
    throw new Error('useTheme must be used within ThemeProvider')
  }
  return ctx
}
